import React, { FC, useEffect, useState } from 'react';
import {
  IndentChoice,
  jsonPathJoin,
  matchesQuery,
  subtreeHasQuery,
  typeLabel,
  valuePreview,
} from './jsonUtils';

export type ExpandMode = 'default' | 'all' | 'none';

export type TreePick = {
  key: string;
  path: string;
  value: any;
};

type Props = {
  value: any;
  indent: IndentChoice;
  search: string;
  expandMode: ExpandMode;
  expandNonce: number;
  selectedPath: string;
  onSelect: (pick: TreePick) => void;
  touch?: boolean;
  openDepth?: number;
};

const PAGE = 80;
const MAX_DEPTH = 28;
const PREVIEW_LEN = 80;

function colorForType(t: string): string {
  if (t === 'string') return '#1b7f4e';
  if (t === 'number') return '#1565c0';
  if (t === 'boolean') return '#6a1b9a';
  if (t === 'null') return '#78909c';
  return '#37474f';
}

const Node: FC<{
  name: string;
  value: any;
  path: string;
  isIndex: boolean;
  depth: number;
  indent: IndentChoice;
  search: string;
  expandMode: ExpandMode;
  expandNonce: number;
  selectedPath: string;
  onSelect: (pick: TreePick) => void;
  touch: boolean;
  openDepth: number;
}> = ({
  name,
  value,
  path,
  isIndex,
  depth,
  indent,
  search,
  expandMode,
  expandNonce,
  selectedPath,
  onSelect,
  touch,
  openDepth,
}) => {
  const isContainer = value !== null && typeof value === 'object';
  const q = search.trim();
  const selfMatch = matchesQuery(name, isContainer ? '' : value, q) || (isContainer && matchesQuery(name, '', q));
  const childMatch = isContainer && q ? subtreeHasQuery(value, q) : false;
  const shouldOpen =
    expandMode === 'all'
      ? true
      : expandMode === 'none'
        ? depth === 0
        : q
          ? childMatch || depth === 0
          : depth < openDepth;

  const [open, setOpen] = useState(shouldOpen);
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    setOpen(shouldOpen);
    setShown(PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandNonce, expandMode, q]);

  const selected = selectedPath === path;
  const row: any = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: touch ? 14 : 13,
    lineHeight: 1.4,
  };

  if (depth > MAX_DEPTH) {
    return <div style={row}>… quá sâu</div>;
  }

  const labelText = isIndex ? `[${name}]` : name;
  const t = typeLabel(value);
  const preview = isContainer
    ? Array.isArray(value)
      ? `{${value.length}}`
      : `{${Object.keys(value).length}}`
    : t === 'string'
      ? JSON.stringify(value)
      : valuePreview(value);
  const shortPreview = preview.length > PREVIEW_LEN ? `${preview.slice(0, PREVIEW_LEN)}…` : preview;

  const pick = () => onSelect({ key: name, path, value });

  const wrap: any = {
    display: 'flex',
    alignItems: 'stretch',
    background: selected ? '#e3f2fd' : selfMatch ? '#fff8e1' : 'transparent',
    borderRadius: 6,
    marginBottom: 1,
  };

  const chevron: any = {
    border: 'none',
    background: 'transparent',
    cursor: isContainer ? 'pointer' : 'default',
    minWidth: touch ? 40 : 28,
    minHeight: touch ? 40 : 28,
    padding: 0,
    color: isContainer ? '#37474f' : '#cfd8dc',
    fontSize: 12,
    flexShrink: 0,
  };

  const bodyBtn: any = {
    ...row,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left' as const,
    flex: 1,
    minWidth: 0,
    minHeight: touch ? 40 : 28,
    padding: '4px 6px 4px 0',
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
  };

  const line = (
    <div style={wrap}>
      <button
        type="button"
        style={chevron}
        aria-label={isContainer ? (open ? 'Thu' : 'Mở') : undefined}
        aria-expanded={isContainer ? open : undefined}
        onClick={(e: any) => {
          e.stopPropagation();
          if (isContainer) setOpen(!open);
          else pick();
        }}
      >
        {isContainer ? (open ? '▼' : '▶') : '•'}
      </button>
      <button type="button" style={bodyBtn} onClick={pick}>
        <span style={{ color: '#5d4037', fontWeight: 700 }}>{labelText}</span>
        <span style={{ color: '#90a4ae' }}>: </span>
        <span style={{ color: isContainer ? '#78909c' : colorForType(t) }}>{shortPreview}</span>
      </button>
    </div>
  );

  if (!isContainer) return line;

  const entries = Array.isArray(value)
    ? value.map((v, i) => ({ key: String(i), val: v, isIndex: true }))
    : Object.keys(value).map((k) => ({ key: k, val: value[k], isIndex: false }));
  const total = entries.length;
  const visible = entries.slice(0, shown);
  const filtered = q
    ? entries.filter((e) => matchesQuery(e.key, e.val, q) || subtreeHasQuery(e.val, q)).slice(0, 200)
    : visible;

  return (
    <div>
      {line}
      {open ? (
        <div
          style={{
            marginLeft: touch ? 8 : 10,
            borderLeft: '1px solid #e0e6ed',
            paddingLeft: touch ? 4 : 6,
          }}
        >
          {filtered.length === 0 && q ? (
            <div style={{ ...row, color: '#78909c', padding: 8 }}>Không khớp</div>
          ) : null}
          {filtered.map((e) => (
            <Node
              key={e.key}
              name={e.key}
              value={e.val}
              path={jsonPathJoin(path, e.isIndex ? Number(e.key) : e.key, e.isIndex)}
              isIndex={e.isIndex}
              depth={depth + 1}
              indent={indent}
              search={search}
              expandMode={expandMode}
              expandNonce={expandNonce}
              selectedPath={selectedPath}
              onSelect={onSelect}
              touch={touch}
              openDepth={openDepth}
            />
          ))}
          {!q && total > shown ? (
            <button
              type="button"
              style={{
                border: 'none',
                background: '#eceff1',
                borderRadius: 8,
                padding: '10px 12px',
                minHeight: 40,
                width: '100%',
                margin: '6px 0',
                fontWeight: 700,
                cursor: 'pointer',
              }}
              onClick={() => setShown(shown + PAGE)}
            >
              Hiện thêm {Math.min(PAGE, total - shown)} / còn {total - shown}
            </button>
          ) : null}
          {q && filtered.length === 200 ? (
            <div style={{ ...row, color: '#78909c', padding: 8 }}>Chỉ hiện 200 kết quả khớp</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const JsonTree: FC<Props> = ({
  value,
  indent,
  search,
  expandMode,
  expandNonce,
  selectedPath,
  onSelect,
  touch = false,
  openDepth = 2,
}) => {
  return (
    <Node
      name="$"
      value={value}
      path="$"
      isIndex={false}
      depth={0}
      indent={indent}
      search={search}
      expandMode={expandMode}
      expandNonce={expandNonce}
      selectedPath={selectedPath}
      onSelect={onSelect}
      touch={touch}
      openDepth={openDepth}
    />
  );
};

export default JsonTree;
